import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { RequestsList } from '@/modules/requests/ui/RequestsList';
import { SearchAndFilter } from '@/app/components/SearchAndFilter';
import { SemanticSearchResults } from '@/app/components/SemanticSearchResults';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import { AuthModal } from '@/app/components/AuthModal';
import { listState } from '@/modules/core/listState';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { trackPageView, trackChangeSort, trackChangeFilter } from '/src/services/analytics/index.js';

export function RequestsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Request list search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterBy, setFilterBy] = useState('all');
  const [isStateRestored, setIsStateRestored] = useState(false);
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  
  // Semantic search state
  const [semanticSearchResults, setSemanticSearchResults] = useState(null);
  const [semanticSearchQuery, setSemanticSearchQuery] = useState('');

  // Scroll position management
  const { restoreScrollPosition, saveScrollPosition } = useScrollPosition('requestspage');

  // Track page view and restore state when returning from request details
  useEffect(() => {
    // Track page view
    trackPageView('requests');
    
    if (!isStateRestored) {
      const savedState = listState.restore();
      if (savedState) {
        console.log('RequestsPage: Restoring saved list state:', savedState);
        setSearchQuery(savedState.searchQuery || '');
        setSortBy(savedState.sortBy || 'newest');
        setFilterBy(savedState.filterBy || 'all');
        
        // Save the scroll position to restore after content loads
        if (savedState.scrollPosition > 0) {
          setSavedScrollPosition(savedState.scrollPosition);
          setShouldRestoreScroll(true);
        }
      }
      setIsStateRestored(true);
    }
  }, [isStateRestored]);

  // Callback for when RequestsList finishes loading
  const handleRequestsLoaded = useCallback(() => {
    if (shouldRestoreScroll && savedScrollPosition > 0) {
      console.log('RequestsPage: Restoring scroll position after content loaded:', savedScrollPosition);
      // Use a longer delay to ensure all content is rendered
      setTimeout(() => {
        window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
        setShouldRestoreScroll(false);
        setSavedScrollPosition(0);
      }, 300);
    }
  }, [shouldRestoreScroll, savedScrollPosition]);

  const handleViewDetails = useCallback((request) => {
    console.log('RequestsPage: Navigating to request details for:', request.id);
    
    // Save current list state before navigating
    listState.save({
      searchQuery,
      sortBy,
      filterBy
    });
    
    navigate(`/request/${request.id}`);
  }, [navigate, searchQuery, sortBy, filterBy]);

  const handleVoteChange = useCallback((requestId, changes) => {
    console.log('RequestsPage: Vote changed for request:', requestId, changes);
    // Trigger a refresh to get updated data
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleSignInNeeded = useCallback(() => {
    console.log('RequestsPage: User needs to sign in');
    setShowAuthModal(true);
  }, []);

  const handleCloseAuthModal = useCallback(() => {
    setShowAuthModal(false);
  }, []);

  const handleSubmitIdea = useCallback(() => {
    if (!user) {
      console.log('RequestsPage: User needs to sign in to submit idea');
      setShowAuthModal(true);
      return;
    }
    
    console.log('RequestsPage: Navigating to submit idea page');
    navigate('/submit');
  }, [user, navigate]);

  const handleSearch = useCallback((query) => {
    console.log('RequestsPage: Clearing search results');
    // Clear semantic search results when search is cleared
    setSemanticSearchResults(null);
    setSemanticSearchQuery('');
    // Also clear regular search query (for cases where we need to show all requests)
    setSearchQuery('');
  }, []);

  const handleSemanticSearch = useCallback((results, query) => {
    console.log('RequestsPage: Semantic search completed:', results);
    setSemanticSearchResults(results);
    setSemanticSearchQuery(query);
    // Clear regular search when doing semantic search
    setSearchQuery('');
  }, []);

  const handleSortChange = useCallback((newSort) => {
    console.log('RequestsPage: Sort changed:', newSort);
    
    // Track sort change
    import('/src/services/analytics/index.js').then(({ trackChangeSort }) => {
      trackChangeSort(newSort);
    });
    
    setSortBy(newSort);
  }, []);

  const handleFilterChange = useCallback((newFilter) => {
    console.log('RequestsPage: Filter changed:', newFilter);
    
    // Track filter change
    import('/src/services/analytics/index.js').then(({ trackChangeFilter }) => {
      trackChangeFilter(newFilter);
    });
    
    setFilterBy(newFilter);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-t-transparent border-purple-400 rounded-full animate-spin opacity-60" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Subtle Background Element */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-indigo-400/8 to-purple-600/8 rounded-full blur-3xl"></div>
      </div>

      <Header onSignInNeeded={handleSignInNeeded} />
      
      <main className="relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Header Section */}
          <div className="pt-16 pb-12 sm:pt-20 sm:pb-16">
            <div className="text-center max-w-4xl mx-auto">
              {/* Title and Subtitle */}
              <div className="mb-12">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
                  <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    See What Apps People Actually Want
                  </span>
                </h1>
                <p className="text-xl sm:text-2xl text-gray-600 font-light leading-relaxed">
                  Vote on ideas you'd use or submit your own
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                <button
                  onClick={handleSubmitIdea}
                  className="group px-8 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Submit New Request
                  </div>
                </button>
                
                <Link
                  to="/"
                  className="group px-8 py-4 bg-white/80 backdrop-blur-sm text-indigo-600 hover:text-indigo-700 font-semibold text-lg rounded-2xl border-2 border-indigo-200 hover:border-indigo-300 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    App Discovery
                  </div>
                </Link>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="mb-8">
            <div className="relative group max-w-4xl mx-auto">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/30 via-purple-500/30 to-pink-500/30 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
              <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-4 sm:p-6 sm:p-8 shadow-xl border border-white/50">
                <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-xl -mx-4 sm:-mx-6 sm:-mx-8 px-4 sm:px-6 sm:px-8 pt-4 sm:pt-6 sm:pt-8 pb-4 sm:pb-6 sm:pb-8 rounded-t-3xl">
                  <SearchAndFilter
                    onSearch={handleSearch}
                    onSemanticSearch={handleSemanticSearch}
                    onSortChange={handleSortChange}
                    onFilterChange={handleFilterChange}
                    currentSort={sortBy}
                    currentFilter={filterBy}
                    currentSearch={searchQuery}
                    isAuthenticated={!!user}
                    hasSemanticResults={!!semanticSearchResults}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Semantic Search Results or Regular Requests List Section */}
          <div className="pb-20">
            {semanticSearchResults ? (
              <SemanticSearchResults 
                results={semanticSearchResults}
                query={semanticSearchQuery}
                onViewDetails={handleViewDetails}
                onSignInNeeded={handleSignInNeeded}
                onClearResults={() => {
                  setSemanticSearchResults(null);
                  setSemanticSearchQuery('');
                }}
              />
            ) : (
              <RequestsList
                sortBy={sortBy}
                searchQuery={searchQuery}
                filterBy={filterBy}
                refreshTrigger={refreshTrigger}
                onViewDetails={handleViewDetails}
                onSignInNeeded={handleSignInNeeded}
                onRequestsLoaded={handleRequestsLoaded}
              />
            )}
          </div>
        </div>
      </main>

      <Footer />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={handleCloseAuthModal} 
      />
    </div>
  );
} 